// eslint-disable-next-line no-unused-vars
import React, { memo, useMemo } from 'react';
import { node, oneOf, oneOfType, string } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import useRouter from 'Hooks/useRouter';
import { NWButton } from 'Components/shared';

import NotFoundBackground from 'Assets/images/notfound_bg.svg';

const StyledNotFound = styled.div`
  display: flex;
  background-color: #F6F7F9;
  margin: 20px;
  padding: 20px 40px;
  border-radius: 20px;
  min-height: 410px;
  position: relative;
  overflow: hidden;

  .notFound {
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    align-self: stretch;
    z-index: 1;
    padding-right: 250px;

    .title {
      color: #3a3a3a;
      font-size: 70px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .description {
      color: #3a3a3a;
      font-size: 40px;
    }

    .caret {
      margin-left: 5px;
      transform: scale(0.875) rotate(-90deg);
    }
  }

  .backgroundImage {
    position: absolute;
    right: 20px;
    top: 20px;
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    padding: 20px;

    .notFound {
      align-items: center;
      gap: 10px;
      padding-right: 0;
      padding-bottom: 400px;

      .title {
        font-size: 40px;
      }

      .description {
        text-align: center;
        font-size: 16px;
      }

      button {
        margin-top: 30px;
        width: 100%;
        max-width: none;
      }
    }

    .backgroundImage {
      right: 20px;
      top: auto;
      bottom: 20px;
    }
  }
`;

const NotFound = memo(({
  type = '404', // default
  title,
  description,
  buttonText,
  url,
}) => {
  const { push } = useRouter();
  const theme = useTheme();
  const { t } = useTranslation();

  const STATUS_CONFIG = useMemo(() => ({
    404: {
      title: t('common.oops'),
      description: t('common.pageNotFound'),
      buttonText: t('common.goToDashboard'),
      url: '/'
    },
    403: {
      title: t('common.oops'),
      description: t('common.notAuthorized'),
      buttonText: t('common.goToDashboard'),
      url: '/'
    }
  }), [t]);

  const defaults = STATUS_CONFIG[type] || STATUS_CONFIG['404'];
  const finalTitle = title ?? defaults.title;
  const finalDescription = description ?? defaults.description;
  const finalButtonText = buttonText ?? defaults.buttonText;
  const finalUrl = url ?? defaults.url;

  return (
    <StyledNotFound theme={ theme }>
      <section className="notFound">
        <h1 className="title">{ finalTitle }</h1>
        <p className="description">{ finalDescription }</p>
        <NWButton
          className="danger"
          onClick={ () => push(finalUrl) }
        >
          { finalButtonText }
          <ChevronDown className="caret" />
        </NWButton>
      </section>
      <div className="backgroundImage">
        <NotFoundBackground />
      </div>
    </StyledNotFound>
  );
});

NotFound.propTypes = {
  type: oneOf(['404', '403']),
  title: oneOfType([string, node]),
  description: oneOfType([string, node]),
  buttonText: oneOfType([string, node]),
  url: string,
};

NotFound.displayName = 'NotFoundComponent';

export default NotFound;
